// Bridge: real catalog entity / manifest fields → the design's mock shapes
// (per the Claude Design data-map). Works on both the manifest entry (flat
// summary fields) and the full entity (structured reviews/awards/programs).

/* eslint-disable @typescript-eslint/no-explicit-any */

import { humanize } from './humanize';

export interface CatMeta {
  key: 'food' | 'stay' | 'sight' | 'view' | 'shop' | 'move';
  label: string;
  cls: string;
}

const CATEGORY: Record<string, CatMeta> = {
  restaurants: { key: 'food', label: 'Eat', cls: 'cat-food' },
  bars: { key: 'food', label: 'Drink', cls: 'cat-food' },
  hotels: { key: 'stay', label: 'Stay', cls: 'cat-stay' },
  attractions: { key: 'sight', label: 'See', cls: 'cat-sight' },
  experiences: { key: 'view', label: 'Do', cls: 'cat-view' },
  shopping: { key: 'shop', label: 'Shop', cls: 'cat-shop' },
  destinations: { key: 'move', label: 'Place', cls: 'cat-move' },
};

export function categoryOf(type: string | undefined): CatMeta {
  return CATEGORY[type ?? ''] ?? { key: 'move', label: type ? humanize(type) : 'Place', cls: 'cat-move' };
}

export function isHotelType(type: string | undefined): boolean {
  return type === 'hotels';
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

export interface GoogleScore {
  r: number;
  n: number | null;
}
export type TabelogTier = 'gold' | 'silver' | 'bronze';
export interface VenueRatings {
  michelin: number;
  bib: boolean;
  tabelog: number | null;
  tabelogGold: boolean;
  /** Tabelog Award tier (juried, independent of the rolling score). */
  tabelogTier: TabelogTier | null;
  /** On a Tabelog per-category Top 100 list. */
  tabelogTop100: boolean;
  google: GoogleScore | null;
  fiftyBest: number | null;
  /** Best James Beard recognition on file, e.g. "JBF Winner 2024". */
  jamesBeard: string | null;
  // Japan-Guide rating (1–3 stars) — highest-trust source for Japan
  // attractions/destinations per the catalog research policy.
  japanGuide: number | null;
}
export interface HotelRatings {
  keys: number | null;
  forbes: number | null;
  google: GoogleScore | null;
  amexFhr: boolean;
  virtuoso: boolean;
  loyalty: string[];
  /** Curated-collection memberships worth a chip: Relais & Châteaux, LHW,
   *  SLH, Mr & Mrs Smith. Read from reviewsTab.editorialRatings.*.member
   *  and programs.bookingChannels.*.enrolled (both shapes exist). */
  memberships: string[];
}

// JBF status rank — the chip shows the strongest recognition on file.
const JBF_RANK: Record<string, number> = { winner: 4, finalist: 3, nominee: 2, semifinalist: 1 };
const JBF_LABEL: Record<string, string> = { winner: 'Winner', finalist: 'Finalist', nominee: 'Nominee', semifinalist: 'Semifinalist' };

function bestJamesBeard(obj: any): string | null {
  const list: any[] = Array.isArray(obj?.awards?.jamesBeard) ? obj.awards.jamesBeard : [];
  let best: { rank: number; status: string; year: number | null } | null = null;
  for (const j of list) {
    const status = String(j?.status ?? '').toLowerCase();
    const rank = JBF_RANK[status] ?? 0;
    if (!rank) continue;
    const year = num(j?.year);
    if (!best || rank > best.rank || (rank === best.rank && (year ?? 0) > (best.year ?? 0))) best = { rank, status, year };
  }
  return best ? `JBF ${JBF_LABEL[best.status]}${best.year ? ` ${best.year}` : ''}` : null;
}

const TABELOG_TIERS = new Set<TabelogTier>(['gold', 'silver', 'bronze']);

// Membership label per editorialRatings / bookingChannels key. Aliases
// collapse onto one label so a hotel carrying both `lhw` and
// `leadingHotels` gets a single chip.
const MEMBERSHIP_LABEL: Record<string, string> = {
  relaisChateaux: 'Relais & Châteaux',
  lhw: 'LHW',
  leadingHotels: 'LHW',
  slh: 'SLH',
  smallLuxuryHotels: 'SLH',
  mrAndMrsSmith: 'Mr & Mrs Smith',
};

export function deriveVenueRatings(obj: any): VenueRatings {
  const michelin = num(obj?.michelinStars) ?? num(obj?.reviews?.ratings?.michelin?.stars) ?? 0;
  const bib =
    obj?.reviews?.ratings?.michelin?.rating === 'Bib Gourmand' ||
    obj?.michelinStatus === 'Bib Gourmand' ||
    obj?.michelin === 'Bib Gourmand';
  const r = num(obj?.googleScore) ?? num(obj?.reviews?.ratings?.googleMaps?.score);
  const n = num(obj?.googleReviews) ?? num(obj?.reviews?.ratings?.googleMaps?.totalReviews);
  const lists: any[] = Array.isArray(obj?.awards?.bestOfLists) ? obj.awards.bestOfLists : [];
  const fifty = lists.find((b) => typeof b?.publication === 'string' && b.publication.includes('50 Best'));
  const tierRaw = String(obj?.tabelogTier ?? obj?.awards?.tabelog?.tier ?? '').toLowerCase() as TabelogTier;
  const tabelogTier = TABELOG_TIERS.has(tierRaw) ? tierRaw : null;
  return {
    michelin: michelin || 0,
    bib: !!bib,
    tabelog: num(obj?.tabelogScore) ?? num(obj?.reviews?.ratings?.tabelog?.score),
    tabelogGold: tabelogTier === 'gold',
    tabelogTier,
    tabelogTop100: obj?.tabelogTop100 === true || obj?.awards?.tabelog?.top100 === true,
    google: r != null ? { r, n } : null,
    fiftyBest: fifty ? (num(fifty.rank) ?? null) : null,
    jamesBeard: bestJamesBeard(obj),
    japanGuide: num(obj?.japanGuide?.rating) ?? num(obj?.reviews?.ratings?.japanGuide?.rating),
  };
}

export function deriveHotelRatings(obj: any): HotelRatings {
  const r =
    num(obj?.reviewsTab?.userScores?.google?.score) ??
    num(obj?.googleScore) ??
    num(obj?.reviews?.ratings?.googleMaps?.score);
  const n =
    num(obj?.reviewsTab?.userScores?.google?.reviewCount) ??
    num(obj?.googleReviews) ??
    num(obj?.reviews?.ratings?.googleMaps?.totalReviews);
  const loyaltyPrograms = obj?.programs?.loyaltyPrograms ?? {};
  const loyalty: string[] = Object.entries(loyaltyPrograms)
    .filter(([, p]: [string, any]) => p && (p.enrolled || p.eligible))
    .map(([k, p]: [string, any]) => p.brand || k)
    .filter(Boolean);
  // Two lived shapes each: the schema says michelinKeys.count / forbesStars
  // .count, but the 2,741-hotel MICHELIN Key import wrote michelinKeys.keys
  // (2,479 entries vs 157 on .count) and hand-built entries carry
  // forbesStars.hotel / .stars. Read all of them.
  const er = obj?.reviewsTab?.editorialRatings ?? {};
  const keys = num(er?.michelinKeys?.count) ?? num(er?.michelinKeys?.keys) ?? num(obj?.michelinKeys);
  const forbes =
    num(er?.forbesStars?.count) ?? num(er?.forbesStars?.hotel) ?? num(er?.forbesStars?.stars) ?? num(er?.forbesStars?.hotelStars);
  const channels = obj?.programs?.bookingChannels ?? {};
  const memberships: string[] = [];
  for (const [k, label] of Object.entries(MEMBERSHIP_LABEL)) {
    const on = er?.[k]?.member === true || channels?.[k]?.enrolled === true;
    if (on && !memberships.includes(label)) memberships.push(label);
  }
  return {
    keys: keys && keys > 0 ? keys : null,
    forbes: forbes && forbes > 0 ? forbes : null,
    google: r != null ? { r, n } : null,
    amexFhr: !!channels?.amexFhr?.enrolled,
    virtuoso: !!channels?.virtuoso?.enrolled,
    loyalty,
    memberships,
  };
}

// ── dated ratings (detail decision strip) ───────────────────────────
// One chip per source with its review count and the day it was read, so a
// score carries its age (#608). Japan-Guide stays on its own 1–3 scale.

export interface DatedRating {
  source: 'Tabelog' | 'Michelin' | 'Google' | 'Japan-Guide';
  /** "4.32", "★★★", "2 Keys". */
  value: string;
  /** "Best of Japan", "Gold". */
  note: string | null;
  /** Review count, when the source has one. */
  count: number | null;
  /** 'YYYY-MM-DD' the value was read. */
  pulledAt: string | null;
}

export const JAPAN_GUIDE_LABEL: Record<number, string> = { 3: 'Best of Japan', 2: 'Highly recommended', 1: 'Recommended' };

function dateOf(...vals: unknown[]): string | null {
  for (const v of vals) if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  return null;
}

export function datedRatings(obj: any, type?: string): DatedRating[] {
  const out: DatedRating[] = [];
  const r = obj?.reviews?.ratings ?? {};
  const hotel = isHotelType(type);
  if (!hotel) {
    const v = deriveVenueRatings(obj);
    const tab = r?.tabelog ?? {};
    if (v.tabelog != null) {
      out.push({
        source: 'Tabelog',
        value: v.tabelog.toFixed(2),
        note: v.tabelogTier ? `${v.tabelogTier[0].toUpperCase()}${v.tabelogTier.slice(1)}` : null,
        count: num(tab?.totalReviews) ?? num(obj?.tabelogReviews),
        pulledAt: dateOf(tab?.pulledAt, tab?.dataCollectedOn),
      });
    }
    const mich = r?.michelin ?? {};
    if (v.michelin > 0 || v.bib) {
      out.push({
        source: 'Michelin',
        value: v.michelin > 0 ? '★'.repeat(v.michelin) : 'Bib',
        note: v.michelin > 0 ? `${v.michelin} star${v.michelin > 1 ? 's' : ''}` : 'Bib Gourmand',
        count: null,
        pulledAt: dateOf(mich?.pulledAt, mich?.dataCollectedOn),
      });
    }
  } else {
    const h = deriveHotelRatings(obj);
    const er = obj?.reviewsTab?.editorialRatings?.michelinKeys ?? {};
    if (h.keys) {
      out.push({
        source: 'Michelin',
        value: `${h.keys} Key${h.keys > 1 ? 's' : ''}`,
        note: null,
        count: null,
        pulledAt: dateOf(er?.pulledAt, er?.dataCollectedOn),
      });
    }
  }
  const g = hotel ? deriveHotelRatings(obj).google : deriveVenueRatings(obj).google;
  if (g) {
    const gm = r?.googleMaps ?? {};
    const gh = obj?.reviewsTab?.userScores?.google ?? {};
    out.push({
      source: 'Google',
      value: g.r.toFixed(1),
      note: null,
      count: g.n,
      pulledAt: dateOf(gh?.pulledAt, gm?.pulledAt, gm?.dataCollectedOn),
    });
  }
  const jgBlock = obj?.japanGuide ?? r?.japanGuide ?? {};
  const jg = num(jgBlock?.rating) ?? num(jgBlock?.region?.rating) ?? num(obj?.japanGuideRating);
  if (jg != null && jg >= 1 && jg <= 3) {
    out.push({
      source: 'Japan-Guide',
      value: '★'.repeat(jg),
      note: JAPAN_GUIDE_LABEL[jg] ?? null,
      count: null,
      pulledAt: dateOf(jgBlock?.pulledAt, jgBlock?.region?.pulledAt, jgBlock?.dataCollectedOn),
    });
  }
  return out;
}

// ── policy ──────────────────────────────────────────────────────────
export type PolicyKey = 'closed' | 'referral' | 'invite' | 'phone' | 'online' | 'timed' | 'walkup';
export type PolicyTone = 'hard' | 'mid' | 'ok' | 'easy';
export const POLICY_META: Record<PolicyKey, { label: string; tone: PolicyTone }> = {
  closed: { label: 'Not taking bookings', tone: 'hard' },
  referral: { label: 'Referral only', tone: 'hard' },
  invite: { label: 'Concierge', tone: 'hard' },
  phone: { label: 'Phone', tone: 'hard' },
  online: { label: 'Books online', tone: 'ok' },
  timed: { label: 'Timed entry', tone: 'mid' },
  walkup: { label: 'Walk-up', tone: 'easy' },
};

// "online reservations are currently SUSPENDED", "not accepting new bookings".
const SUSPENDED_RE =
  /\b(?:reservations?|bookings?)\b[^.;]{0,40}\b(?:suspended|paused|halted)\b|\bnot (?:currently )?(?:taking|accepting) (?:any |new )?(?:reservations|bookings)\b/i;
const REFERRAL_RE = /referral|introduction[- ]only|regulars[- ]only|members[- ]only|invitation[- ]only|一見さんお断り/i;

/**
 * The header chip, read from the reservation block. `policy` (the
 * canonical field) decides whether a booking is needed at all; `required:
 * false` alone no longer means walk-up, since Kikunoi Honten carries it
 * with policy "recommended" and "walk-ins are not practically possible".
 */
export function policyKeyOf(obj: any, type?: string): PolicyKey | null {
  const resv = obj?.reservation && typeof obj.reservation === 'object' ? obj.reservation : null;
  const diff = String(resv?.difficulty ?? obj?.reservationDifficulty ?? '').toLowerCase();
  const platform = String(resv?.platform ?? '').toLowerCase();
  const required = resv?.required ?? obj?.reservationRequired;
  const policyEnum = resv?.policy ?? obj?.reservationPolicy;
  // no reservation signal at all → no tag
  if (!diff && !platform && required == null && !policyEnum) return null;
  const notes = [resv?.walkInNotes, resv?.notes].filter((x) => typeof x === 'string').join(' ');
  if (SUSPENDED_RE.test(notes)) return 'closed';
  if (resv?.membersOnly === true || REFERRAL_RE.test(platform)) return 'referral';
  const walkUp =
    diff.includes('walk') ||
    policyEnum === 'not_accepted' ||
    policyEnum === 'optional' ||
    (required === false && policyEnum !== 'recommended' && policyEnum !== 'required');
  if (walkUp) return 'walkup';
  if (platform.includes('phone')) return 'phone';
  if (type === 'attractions' || platform.includes('timed')) return 'timed';
  if (
    required &&
    (platform === '' || platform.includes('concierge') || platform.includes('invite') || platform.includes('direct')) &&
    (diff.includes('hard') || diff.includes('extreme'))
  ) {
    return 'invite';
  }
  return 'online';
}

/** A place nobody can book right now: referral only, or not taking bookings.
 *  Its surfaces show the policy label instead of Book / Add to a day / Track. */
export function isUnbookable(policy: PolicyKey | null | undefined): policy is 'referral' | 'closed' {
  return policy === 'referral' || policy === 'closed';
}

// ── price ───────────────────────────────────────────────────────────
// A price tier 0–4 (0 = free) and its glyphs in the place's own currency.
// A stated number wins over a glyph label: "¥22,000–44,000" is a ¥¥¥¥
// dinner, not ¥ (counting the ¥ signs in that range gave Gion Sasaki and
// Hyotei one ¥). Venue tiers read the top of the per-person range; hotel
// tiers read the nightly floor.

/** Tier glyphs in yen, for callers with no place to take a country from. */
export const PRICE_LABELS = ['Free', '¥', '¥¥', '¥¥¥', '¥¥¥¥'];

// Upper bounds of tiers 1–3 (tier 4 above the last), per currency.
const VENUE_TIERS: Record<string, number[]> = { JPY: [3000, 10000, 30000], USD: [20, 50, 120] };
const HOTEL_TIERS: Record<string, number[]> = { JPY: [20000, 50000, 100000], USD: [200, 450, 900] };
// Rough USD value of one unit, only to place other currencies on the USD scale.
const USD_PER: Record<string, number> = {
  EUR: 1.1, GBP: 1.3, CHF: 1.1, CAD: 0.73, AUD: 0.66, NZD: 0.6, KRW: 0.00075, TWD: 0.031, HKD: 0.13,
  SGD: 0.75, THB: 0.028, CNY: 0.14, MXN: 0.055, JPY: 0.0068,
};

// Currency markers, longest first so "NT$" wins over "$".
const MARKERS: Array<[string, string]> = [
  ['NT$', 'TWD'], ['HK$', 'HKD'], ['MX$', 'MXN'], ['NZ$', 'NZD'], ['C$', 'CAD'], ['A$', 'AUD'], ['S$', 'SGD'],
  ['JPY', 'JPY'], ['USD', 'USD'], ['EUR', 'EUR'], ['GBP', 'GBP'], ['KRW', 'KRW'], ['CHF', 'CHF'], ['TWD', 'TWD'],
  ['¥', 'JPY'], ['€', 'EUR'], ['£', 'GBP'], ['₩', 'KRW'], ['฿', 'THB'], ['$', 'USD'],
];
const MARKER_SRC = MARKERS.map(([m]) => m.replace(/\$/g, '\\$')).join('|');
const NUM_SRC = '(\\d[\\d,]*(?:\\.\\d+)?)(k)?';
// "¥22,000–44,000", "JPY 50,000 - JPY 59,999", "$16–$24", "¥30k", or "30,000円".
const AMOUNT_RE = new RegExp(
  `(${MARKER_SRC})\\s*${NUM_SRC}(?:\\s*[–—-]\\s*(?:${MARKER_SRC})?\\s*${NUM_SRC})?|(\\d[\\d,]*)\\s*円`,
  'gi',
);

function currencyOfMarker(m: string): string {
  const hit = MARKERS.find(([k]) => k.toLowerCase() === m.toLowerCase());
  return hit ? hit[1] : 'USD';
}

function amountOf(raw: string | undefined, k: string | undefined): number | null {
  if (!raw) return null;
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  return k ? n * 1000 : n;
}

/** The highest amount a price string states, with its currency. */
export function statedPrice(text: unknown): { amount: number; currency: string } | null {
  if (typeof text !== 'string' || !text) return null;
  let best: { amount: number; currency: string } | null = null;
  for (const m of text.matchAll(AMOUNT_RE)) {
    const currency = m[6] ? 'JPY' : currencyOfMarker(m[1] ?? '$');
    for (const n of [amountOf(m[2], m[3]), amountOf(m[4], m[5]), amountOf(m[6], undefined)]) {
      if (n == null) continue;
      if (!best) best = { amount: n, currency };
      else if (best.currency === currency && n > best.amount) best.amount = n;
    }
  }
  return best;
}

function tierOfAmount(amount: number, currency: string, hotel: boolean): number {
  const table = hotel ? HOTEL_TIERS : VENUE_TIERS;
  let bounds = table[currency];
  let value = amount;
  if (!bounds) {
    bounds = table.USD;
    value = amount * (USD_PER[currency] ?? 1);
  }
  const i = bounds.findIndex((b) => value < b);
  return i < 0 ? 4 : i + 1;
}

function glyphCount(label: string): number {
  // "$-$$" and "$$ – $$$" name a span; take its top.
  let best = 0;
  for (const run of label.match(/\$+|¥+|€+|£+|₩+/g) ?? []) best = Math.max(best, run.length);
  return best;
}

/**
 * Price tier 0–4, or null. `type` is the catalog type ('hotels' reads the
 * nightly rate); a manifest entry's own `type` stands in when it is omitted.
 */
export function priceTier(obj: any, type?: string): number | null {
  const catType = type ?? (typeof obj?.type === 'string' ? obj.type : undefined);
  const hotel = catType === 'hotels';
  const from = num(obj?.price?.from);
  const to = num(obj?.price?.to);
  if (from != null && from > 0) {
    const cur = typeof obj?.price?.currency === 'string' ? obj.price.currency.toUpperCase() : 'JPY';
    return tierOfAmount(hotel ? from : (to ?? from), cur, hotel);
  }
  const range = typeof obj?.priceRange === 'string' ? obj.priceRange : '';
  const stated = statedPrice(range) ?? statedPrice(obj?.price?.label);
  if (stated) return tierOfAmount(stated.amount, stated.currency, hotel);
  const label = String(obj?.priceLabel ?? obj?.price?.label ?? range ?? '');
  const glyphs = glyphCount(label);
  if (glyphs > 0) return Math.min(glyphs, 4);
  if (/\bfree\b/i.test(label)) return 0;
  return null;
}

// Country → the glyph its prices print with.
const GLYPH_OF_COUNTRY: Record<string, string> = {
  japan: '¥', china: '¥', 'south korea': '₩', korea: '₩', 'united kingdom': '£', uk: '£', thailand: '฿',
  india: '₹', turkey: '₺', vietnam: '₫', philippines: '₱',
};
const EURO_COUNTRIES = new Set([
  'france', 'italy', 'spain', 'germany', 'netherlands', 'austria', 'belgium', 'portugal', 'greece', 'ireland',
  'finland', 'luxembourg', 'malta', 'slovenia', 'slovakia', 'lithuania', 'latvia', 'estonia', 'croatia', 'monaco',
  'andorra', 'montenegro', 'cyprus', 'french west indies',
]);

/** The glyph a place's price prints with: ¥ in Japan, $ in the US, else the local one or $. */
export function priceGlyph(country: unknown): string {
  const c = typeof country === 'string' ? country.trim().toLowerCase() : '';
  if (!c) return '$';
  if (GLYPH_OF_COUNTRY[c]) return GLYPH_OF_COUNTRY[c];
  if (EURO_COUNTRIES.has(c)) return '€';
  return '$';
}

/** Tier glyphs for a country: priceGlyphs(3, 'Japan') → '¥¥¥'. */
export function priceGlyphs(tier: number, country?: unknown): string {
  return tier <= 0 ? 'Free' : priceGlyph(country).repeat(tier);
}

export function priceLabelOf(obj: any, type?: string): string | null {
  const tier = priceTier(obj, type);
  return tier == null ? null : priceGlyphs(tier, obj?.country);
}

/** The stated yen price ("¥22,000–44,000", "¥128,000–430,000 a night"), or null. */
export function yenPriceText(obj: any): string | null {
  const range = typeof obj?.priceRange === 'string' ? obj.priceRange : null;
  if (range && /¥|円|JPY/.test(range)) {
    const first = range.split(/;|\n/)[0].trim();
    return first.length > 90 ? `${first.slice(0, 88).trimEnd()}…` : first;
  }
  const p = obj?.price;
  const from = num(p?.from);
  if (from != null && String(p?.currency ?? 'JPY').toUpperCase() === 'JPY') {
    const to = num(p?.to);
    const perNight = typeof p?.basis === 'string' && /night/.test(p.basis) ? ' a night' : '';
    return `¥${from.toLocaleString('en-US')}${to && to !== from ? `–${to.toLocaleString('en-US')}` : ''}${perNight}`;
  }
  return null;
}

// Compact pedigree chips for a card — the awards and memberships that
// decide whether a place is worth the detour, strongest first. Scores
// (Google, Tabelog points) are deliberately NOT here; they're ratings, not
// recognition, and the card rows render them separately. Works on the
// manifest entry (flat fields) or the full entity (structured awards) —
// pass the full entity when you have it, the manifest lacks Keys/Forbes.
export function recognitionChips(obj: any, type?: string): string[] {
  const out: string[] = [];
  if (isHotelType(type)) {
    const h = deriveHotelRatings(obj);
    if (h.keys) out.push(`${h.keys} Michelin Key${h.keys > 1 ? 's' : ''}`);
    if (h.forbes) out.push(`Forbes ${h.forbes}★`);
    out.push(...h.memberships);
    if (h.amexFhr) out.push('Amex FHR');
    if (h.virtuoso) out.push('Virtuoso');
    return out;
  }
  const v = deriveVenueRatings(obj);
  if (v.michelin) out.push(`Michelin ${v.michelin}★`);
  if (v.bib) out.push('Bib Gourmand');
  if (v.tabelogTier) out.push(`Tabelog ${v.tabelogTier[0].toUpperCase()}${v.tabelogTier.slice(1)}`);
  if (v.tabelogTop100) out.push('Tabelog Top 100');
  if (v.fiftyBest) out.push(`#${v.fiftyBest} World`);
  if (v.jamesBeard) out.push(v.jamesBeard);
  return out;
}

// Personal-history chips from the Google Maps Takeout import
// (personalData.google on ~2.9k entries: `starred` = the "Favorite places"
// list; the "Maps history" list = Google's visited-places history, dated by
// savedToMapsAt) plus the hand-written previousVisit block. Full entity only —
// the manifest doesn't carry personalData.
export function personalChips(obj: any): string[] {
  const out: string[] = [];
  const g = obj?.personalData?.google ?? {};
  if (g?.starred === true) out.push('★ Favorite');
  const lists: string[] = Array.isArray(g?.lists) ? g.lists.map(String) : [];
  const pv = obj?.previousVisit ?? {};
  const beenYear = num(pv?.year) ?? (lists.includes('Maps history') && typeof g?.savedToMapsAt === 'string' ? num(g.savedToMapsAt.slice(0, 4)) : null);
  if (pv?.visited === true || lists.includes('Maps history')) out.push(beenYear ? `Been · ${beenYear}` : 'Been');
  return out;
}

// short recognition badge for a card (bottom-right overlay) — the strongest
// pedigree chip.
export function recognitionBadge(obj: any, type?: string): string | null {
  return recognitionChips(obj, type)[0] ?? null;
}
